import { lazy, Suspense, FC, ReactNode, useState } from 'react';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import { getHolidayShippingInfo } from './helpers/holidayDeliveryHelpers';

// types
import { PdpSharedShippingHolidayDelivery_sku as Sku } from './__generated__/PdpSharedShippingHolidayDelivery_sku.graphql';
import { PdpSharedShippingHolidayDelivery_item as Item } from './__generated__/PdpSharedShippingHolidayDelivery_item.graphql';
import { PdpSharedShippingHolidayDelivery_viewer as Viewer } from './__generated__/PdpSharedShippingHolidayDelivery_viewer.graphql';

// styles
import styles from './PdpSharedShippingHolidayDelivery.scss';

const PdpSharedShippingHolidayDeliveryModal = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryModal" */ './PdpSharedShippingHolidayDeliveryModal'
        )
);

const PdpSharedShippingHolidayDeliveryTooltip = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryTooltip" */ './PdpSharedShippingHolidayDeliveryTooltip'
        )
);

const BowIcon = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingHolidayDeliveryBowIcon" */ 'dibs-icons/exports/Bow'
        )
);

const trackHolidayDelivery = ({
    isFreeShipping,
    standardShipping,
}: {
    isFreeShipping: boolean;
    standardShipping: boolean;
}): void => {
    let shippingType = 'expedited';
    if (isFreeShipping) {
        shippingType = 'free';
    } else if (standardShipping) {
        shippingType = 'standard';
    }

    trackEvent({
        category: 'product',
        label: `${shippingType} shipping`,
        action: 'holiday get in time shipping displayed',
    });
};

type Props = {
    sku: Sku;
    item: Item;
    viewer: Viewer;
    isClient: boolean;
    useIcon?: boolean;
    children: ReactNode;
    useTooltipDetails?: boolean;
};

export const PdpSharedShippingHolidayDeliveryComponent: FC<Props> = ({
    sku,
    item,
    viewer,
    useIcon,
    isClient,
    useTooltipDetails,
}) => {
    const [hasFiredTracking, setHasFiredTracking] = useState<boolean>(false);
    const { relevantShipping, transactionWarnings } = viewer || {};
    const { hasRestrictedMaterials, isCheckoutBlocked } = transactionWarnings || {};
    const shipmentDeliveryEstimations = sku
        ? sku?.holidayShippingEstimations
        : item?.holidayShippingEstimations;
    const { displayMessage, termsAndConditions = null, validHolidayShippingDays = null } =
        shipmentDeliveryEstimations || {};

    const holidayShippingMethods = getHolidayShippingInfo({
        validHolidayShippingDays,
        shipmentQuotes: relevantShipping?.shipmentQuotes || [],
    });

    if (
        !holidayShippingMethods.length ||
        hasRestrictedMaterials ||
        isCheckoutBlocked ||
        !displayMessage
    ) {
        return null;
    }

    const {
        totalAmount,
        isComplimentary,
        estimatedDeliveryDay,
        buyerServiceMethodDisplay,
    } = holidayShippingMethods[0];

    const isFreeShipping = isComplimentary || totalAmount?.amount === 0;

    if (!hasFiredTracking && isClient) {
        trackHolidayDelivery({
            isFreeShipping,
            standardShipping: buyerServiceMethodDisplay === 'Standard',
        });
        setHasFiredTracking(true);
    }

    return (
        <div className={styles.container}>
            {useIcon && isClient && (
                <Suspense fallback={null}>
                    <BowIcon className={styles.iconComponent} />
                </Suspense>
            )}
            <span className={styles.text}>
                <FormattedMessage
                    id="pdp.sharedHolidayShipping.estimatedDelivery"
                    defaultMessage="<b>{displayMessage}:</b>{isFreeShipping, select, 
                        true { Free}
                        other {}
                        } {buyerServiceMethodDisplay} Shipping arrives by {estimatedDeliveryDay}."
                    values={{
                        isFreeShipping,
                        displayMessage,
                        buyerServiceMethodDisplay,
                        b: (chunks: ReactNode[]) => (
                            <span className={styles.boldText}>{chunks}</span>
                        ),
                        estimatedDeliveryDay,
                    }}
                />
                {useTooltipDetails && isClient && (
                    <Suspense fallback={null}>
                        <PdpSharedShippingHolidayDeliveryTooltip
                            termsAndConditions={termsAndConditions}
                        />
                    </Suspense>
                )}
                {!useTooltipDetails && isClient && (
                    <Suspense fallback={null}>
                        <PdpSharedShippingHolidayDeliveryModal
                            termsAndConditions={termsAndConditions}
                        />
                    </Suspense>
                )}
            </span>
        </div>
    );
};

export const PdpSharedShippingHolidayDelivery = createFragmentContainer(
    PdpSharedShippingHolidayDeliveryComponent,
    {
        viewer: graphql`
            fragment PdpSharedShippingHolidayDelivery_viewer on Viewer {
                transactionWarnings(itemId: $itemId, country: $userCountryCode)
                @include(if: $isClient) {
                    hasRestrictedMaterials
                    isCheckoutBlocked
                }
                relevantShipping(itemId: $itemId, buyerId: $userId) @include(if: $isClient) {
                    shipmentQuotes {
                        serviceId
                        isComplimentary
                        buyerServiceMethodDisplay
                        totalAmount {
                            amount
                        }
                        serviceMethod {
                            serviceType
                            serviceMethodType
                            region {
                                code
                            }
                        }
                    }
                }
            }
        `,
        item: graphql`
            fragment PdpSharedShippingHolidayDelivery_item on Item {
                holidayShippingEstimations(timezoneOffset: $timezoneOffset)
                @include(if: $isClient) {
                    displayMessage
                    termsAndConditions
                    validHolidayShippingDays {
                        shipmentQuoteId
                        estimatedDeliveryDay
                    }
                }
            }
        `,
        sku: graphql`
            fragment PdpSharedShippingHolidayDelivery_sku on ItemSkuType {
                holidayShippingEstimations(timezoneOffset: $timezoneOffset)
                @include(if: $isClient) {
                    displayMessage
                    termsAndConditions
                    validHolidayShippingDays {
                        shipmentQuoteId
                        estimatedDeliveryDay
                    }
                }
            }
        `,
    }
);
